import { useTranslate } from "react-admin";

import { Button } from "../MuiGenerals";
import { RESCAN_LOCK_TIME_MS } from "../../utils/constant";
import { useZusDialogStore } from "../../zustand/store";

import InfoBanner from "./InfoBanner";
import RiskLevelBadge from "./RiskLevelBadge";

interface RescanConfirmProps {
  onConfirm: () => void;
}

// First step in the rescan flow. Shown when the wallet is not in cooldown.
// Prompts the user to confirm triggering a fresh query to Merkle Science.
const RescanConfirm = ({ onConfirm }: RescanConfirmProps) => {
  const translate = useTranslate();
  const t = (key: string, options?: Record<string, number>) =>
    translate(`walletManagement.${key}`, options);
  const zusDialog = useZusDialogStore();
  const handleClose = () => zusDialog.close();
  // Prefer the API-provided lock duration, fall back to the compile-time
  // constant when the API hasn't responded yet.
  const lockDuration =
    (zusDialog.meta?.lock_time_ms as number | undefined) ?? RESCAN_LOCK_TIME_MS;
  const rescanLockMinutes = lockDuration / (60 * 1000);

  const {
    display_name,
    wallet_address,
    chain_name,
    current_risk_level,
    last_rescan_time,
  } = zusDialog.meta || {};

  return (
    <>
      <div style={{ padding: "16px 24px 12px" }}>
        <div style={rowStyle}>
          <div style={labelStyle}>{t("display_name")}</div>
          <div style={valueStyle}>{display_name}</div>
        </div>
        <div style={rowStyle}>
          <div style={labelStyle}>{t("walletAddress")}</div>
          <div style={valueStyle}>{wallet_address}</div>
        </div>
        <div style={rowStyle}>
          <div style={labelStyle}>{t("chain_name")}</div>
          <div style={valueStyle}>{chain_name}</div>
        </div>
        <div style={rowStyle}>
          <div style={labelStyle}>{t("current_risk_level")}</div>
          <div style={valueStyle}>
            <RiskLevelBadge riskLevel={current_risk_level} />
          </div>
        </div>
        <div style={rowStyle}>
          <div style={labelStyle}>{t("last_rescan")}</div>
          <div style={valueStyle}>{last_rescan_time || t("never")}</div>
        </div>
      </div>

      <div style={{ padding: "0 24px 16px" }}>
        <InfoBanner tone="warning">
          {t("rescan_warning_step1", { rescanLockMinutes })}
        </InfoBanner>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          padding: "8px 24px 20px",
        }}
      >
        <Button color="secondary" variant="contained" onClick={onConfirm}>
          {translate("common.confirm")}
        </Button>
        <Button color="error" variant="contained" onClick={handleClose}>
          {translate("common.cancel")}
        </Button>
      </div>
    </>
  );
};

const rowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "120px 1fr",
  gap: "12px",
  padding: "6px 0",
  alignItems: "center",
};

const labelStyle: React.CSSProperties = {
  color: "#8C8C8C",
  fontSize: "13px",
};

const valueStyle: React.CSSProperties = {
  color: "#262626",
  fontSize: "13px",
  wordBreak: "break-all",
};

export default RescanConfirm;
